import {useMemo} from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

function agencyLabel(agency) {
    const agencyName = (
        agency.cos_customer_name
        || agency.agent_name
        || agency.username
        || "Unnamed Agency"
    ).trim();
    const username = (agency.username || "").trim();
    const usernameSuffix = username && username !== agencyName
        ? ` — ${username}`
        : "";

    return `${agencyName}${usernameSuffix} (${agency.cos_customer_id})`;
}

export function filterAgencies(options, {inputValue}) {
    const prefix = inputValue.trim().toLocaleLowerCase();
    if (!prefix) {
        return options;
    }

    return options.filter(agency => [
        agency.cos_customer_name,
        agency.agent_name,
        agency.username,
    ].some(value => value?.trim().toLocaleLowerCase().startsWith(prefix)));
}

export default function AgencyAutocomplete({
    agencies,
    value,
    onChange,
    required = false,
    disableClearable = false,
    helperText = "Type the start of an Agency name or username.",
}) {
    const options = useMemo(() => {
        const agenciesByCustomerId = new Map();

        agencies.forEach(agency => {
            if (agency.cos_customer_id && !agenciesByCustomerId.has(agency.cos_customer_id)) {
                agenciesByCustomerId.set(agency.cos_customer_id, agency);
            }
        });

        return Array.from(agenciesByCustomerId.values())
            .sort((left, right) => agencyLabel(left).localeCompare(agencyLabel(right)));
    }, [agencies]);

    const selectedAgency = options.find(
        agency => agency.cos_customer_id === value
    ) || null;

    return (
        <Autocomplete
            autoHighlight
            openOnFocus
            options={options}
            value={selectedAgency}
            onChange={(_event, agency) => onChange(agency?.cos_customer_id || "")}
            getOptionLabel={agencyLabel}
            getOptionKey={agency => agency.cos_customer_id}
            isOptionEqualToValue={(option, selected) =>
                option.cos_customer_id === selected.cos_customer_id}
            filterOptions={filterAgencies}
            disableClearable={disableClearable}
            noOptionsText="No matching Agency"
            renderInput={params => (
                <TextField
                    {...params}
                    label="Agency"
                    name="agency_search"
                    placeholder="Search Agencies…"
                    required={required}
                    helperText={helperText}
                    size="small"
                    inputProps={{
                        ...params.inputProps,
                        autoComplete: "off",
                        spellCheck: false,
                    }}
                />
            )}
        />
    );
}
